/**
 * Photo Viewer Core — Shared state and logic for mobile + desktop photo viewers.
 *
 * Platform composers (mobile/desktop) spread this into their Alpine data objects
 * and add platform-specific behavior (touch/swipe, keyboard nav, etc.).
 */

import { toggleLikeApi } from './alpine-like-button.js';

// Close routing: goBack() uses history.back() only when an internal
// non-viewer page is known to sit directly beneath the current history
// entry; otherwise it navigates to the album. That fact belongs to the
// individual history entry: photo-to-photo navigation replaces the current
// entry (location.replace), and the new document's referrer is the previous
// *photo page*, which says nothing about what lies beneath. The flag is
// therefore carried across those replaces in a query param and stripped
// from the address bar on read, so reloads of a replaced entry and shared
// links never inherit a stale value. A same-origin referrer alone is also
// not sufficient evidence: a link opened in a new tab carries the referrer
// but starts a fresh history stack, hence the history.length check.
const OPENER_PARAM = 'pv_oih';
const VIEWER_PATH = '/app/photo';

// Memoized per document: photo pages construct both the mobile and desktop
// viewer components, and the first resolution strips the param, so a second
// uncached resolution would disagree with the first.
let _openerInHistoryCache = null;

function isSameOriginReferrer() {
    try {
        return !!document.referrer && new URL(document.referrer).origin === window.location.origin;
    } catch {
        return false;
    }
}

function resolveOpenerInHistory() {
    if (_openerInHistoryCache === null) {
        _openerInHistoryCache = computeOpenerInHistory();
    }
    return _openerInHistoryCache;
}

function computeOpenerInHistory() {
    try {
        // history.state survives reloads and history traversal, so a value
        // persisted when the param was stripped outlives this document.
        if (history.state && typeof history.state.pvOih === 'boolean') {
            return history.state.pvOih;
        }
        const url = new URL(window.location.href);
        if (url.searchParams.has(OPENER_PARAM)) {
            // The param is only trusted on same-origin navigations: a crafted
            // external link must not be able to route close to history.back().
            const openerInHistory = url.searchParams.get(OPENER_PARAM) === '1'
                && isSameOriginReferrer();
            url.searchParams.delete(OPENER_PARAM);
            history.replaceState({ ...(history.state || {}), pvOih: openerInHistory }, '', url.toString());
            return openerInHistory;
        }
        if (!isSameOriginReferrer()) return false;
        return new URL(document.referrer).pathname !== VIEWER_PATH
            && window.history.length > 1;
    } catch {
        return false;
    }
}

function withOpenerParam(url, openerInHistory) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${OPENER_PARAM}=${openerInHistory ? '1' : '0'}`;
}

export function photoViewerCore(config) {
    const _albumId = String(config.albumId || '');
    const _userId = String(config.userId || '');
    const _prevPid = String(config.prevPid || '');
    const _nextPid = String(config.nextPid || '');
    const _base = `/app/photo?aid=${_albumId}&uid=${_userId}&pid=`;

    return {
        // Photo data
        photoUrl: config.photoUrl,
        photoId: String(config.photoId),
        prevUrl: config.prevUrl || (_prevPid ? _base + _prevPid : null),
        nextUrl: config.nextUrl || (_nextPid ? _base + _nextPid : null),
        albumUrl: config.albumUrl,
        username: config.username,
        userUrl: config.userUrl,
        description: config.description || '',
        timeAgo: config.timeAgo || '',

        // Like state
        isLiked: config.isLiked || false,
        likeCount: config.likeCount || 0,
        isLikeLoading: false,

        // Comment count
        commentCount: config.commentCount || 0,

        // Permissions
        isOwner: config.isOwner || false,
        isModerator: config.isModerator || false,
        canPhotoBanOptIn: config.canPhotoBanOptIn || false,

        // Album cover
        albumCoverId: String(config.albumCoverId || ''),
        get isCover() {
            return this.photoId && this.photoId === this.albumCoverId;
        },
        settingCover: false,

        // Caption editing
        captionEditOpen: false,
        tempCaption: '',
        savingCaption: false,

        // Move to album
        otherAlbums: config.otherAlbums || [],
        moveModalOpen: false,
        movingPhoto: false,

        // Action URLs
        deleteUrl: config.deleteUrl || '',
        deleteConfirmText: config.deleteConfirmText || '',
        tineyeUrl: config.tineyeUrl || '',
        googleUrl: config.googleUrl || '',
        yandexUrl: config.yandexUrl || '',

        // UI state
        commentsOpen: false,
        menuOpen: false,

        // Navigation animation
        isNavigating: false,
        navigateDirection: 0,

        // SPA navigation state
        _prevPid: String(config.prevPid || ''),
        _nextPid: String(config.nextPid || ''),
        _albumId: String(config.albumId || ''),
        _userId: String(config.userId || ''),
        _commentsStale: false,
        _openerInHistory: resolveOpenerInHistory(),

        // --- Preload ---

        _preloadAdjacentImages(cfg) {
            [cfg.prevImageUrl, cfg.nextImageUrl].forEach(url => {
                if (url) new Image().src = url;
            });
        },

        // --- Navigation ---

        goBack() {
            if (window._photoViewerGoingBack) return;
            window._photoViewerGoingBack = true;
            window.removeEventListener('popstate', this._popstateHandler);

            setTimeout(() => {
                if (this._openerInHistory && window.history.length > 1) {
                    history.back();
                } else {
                    window.location.href = this.albumUrl;
                }
                // Reset guard if navigation didn't happen
                setTimeout(() => { window._photoViewerGoingBack = false; }, 500);
            }, 0);
        },

        _hasPid(pid) {
            return !!pid;
        },

        navigateTo(direction) {
            const targetPid = direction > 0 ? this._nextPid : this._prevPid;
            const fallbackUrl = direction > 0 ? this.nextUrl : this.prevUrl;
            if (!this._hasPid(targetPid) && !fallbackUrl) return;

            this.isNavigating = true;
            this.navigateDirection = -direction;

            // Moderators navigate via a full page load so server-rendered
            // moderator-only overlays (the AI-provenance badge) are
            // re-evaluated for each photo. AJAX navigation swaps the photo
            // data client-side without re-running the server render, which
            // would leave the badge showing the previous photo's provenance.
            // Guard on fallbackUrl so a null is never navigated to — it
            // derives from the same _nextPid/_prevPid as targetPid, so it is
            // present whenever there is a real photo to navigate to.
            // location.replace keeps the viewer at a single history entry
            // (matching the AJAX path's history.replaceState), so goBack()'s
            // history.back() returns to the page that opened the viewer
            // instead of stepping back through every viewed photo.
            if (this.isModerator && fallbackUrl) {
                setTimeout(() => { window.location.replace(withOpenerParam(fallbackUrl, this._openerInHistory)); }, 30);
                return;
            }

            if (!this._hasPid(targetPid)) {
                setTimeout(() => { window.location.replace(withOpenerParam(fallbackUrl, this._openerInHistory)); }, 30);
                return;
            }

            this._fetchPhoto(targetPid, direction, fallbackUrl);
        },

        async _fetchPhoto(pid, direction, fallbackUrl) {
            try {
                const resp = await fetch(`/app/helpers/photo?pid=${pid}&t=p`);
                if (!resp.ok) throw new Error('fetch failed');
                const data = await resp.json();

                await new Promise(r => setTimeout(r, 30));

                this._applyPhotoData(data);
                this._resetNavAnimation();

                const pageUrl = `/app/photo?pid=${data.pid}&aid=${this._albumId}&uid=${this._userId}`;
                // Preserve history.state: it carries the close-routing flag
                // (pvOih) for this entry across URL rewrites.
                history.replaceState(history.state, '', pageUrl);

                this._preloadAdjacent(data);
            } catch {
                if (fallbackUrl) {
                    // Photo-to-photo navigation: replace so the viewer keeps a
                    // single history entry and goBack() closes in one step.
                    window.location.replace(withOpenerParam(fallbackUrl, this._openerInHistory));
                } else {
                    this._resetNavAnimation();
                }
            }
        },

        _applyPhotoData(data) {
            this.photoUrl = data.url;
            this.photoId = String(data.pid);
            this.description = data.d || '';
            this.timeAgo = data.a || '';
            this.isLiked = data.user_liked || false;
            this.likeCount = data.likes || 0;
            this.commentCount = data.comments || 0;

            this._prevPid = String(data.p || '');
            this._nextPid = String(data.n || '');

            const base = `/app/photo?aid=${this._albumId}&uid=${this._userId}&pid=`;
            this.prevUrl = this._hasPid(this._prevPid) ? base + this._prevPid : null;
            this.nextUrl = this._hasPid(this._nextPid) ? base + this._nextPid : null;

            this.deleteUrl = `/app/albums?todo=delete_photo&aid=${this._albumId}&pid=${data.pid}`;

            this.tineyeUrl = `http://www.tineye.com/search?url=${data.url}`;
            this.googleUrl = `https://lens.google.com/uploadbyurl?url=${data.url}`;
            this.yandexUrl = `https://www.yandex.com/images/search?rpt=imageview&url=${data.url}`;

            if (this.commentsOpen) {
                this._commentsStale = true;
                this._refetchCommentsOnNav();
            } else {
                this._commentsStale = true;
            }
            this._updateCommentForm(data.pid);
        },

        /**
         * Override in platform composers if comments need to auto-refetch
         * when navigating while comments panel is open (desktop sidebar).
         */
        _refetchCommentsOnNav() {
            this.commentsOpen = false;
        },

        _updateCommentForm(pid) {
            const container = this._getCommentsContainer();
            if (!container) return;
            const form = container.querySelector('.comment-form');
            if (!form) return;
            const entityInput = form.querySelector('input[name="entity_id"]');
            if (entityInput) entityInput.value = pid;
            form.id = `commentForm_${pid}`;
        },

        /**
         * Override in platform composers to return the comments DOM container.
         * Mobile uses $refs.commentsSheet, desktop uses $refs.commentsSidebar.
         */
        _getCommentsContainer() {
            return this.$refs.commentsSheet || this.$refs.commentsSidebar || null;
        },

        _resetNavAnimation() {
            this.isNavigating = false;
            this.navigateDirection = 0;
        },

        _preloadAdjacent(data) {
            [data.n_th?.url, data.p_th?.url, data.n_url, data.p_url].forEach(url => {
                if (url) new Image().src = url;
            });
        },

        // --- Like ---

        async toggleLike() {
            if (this.isLikeLoading) return;
            this.isLikeLoading = true;

            const wasLiked = this.isLiked;
            this.isLiked = !wasLiked;
            this.likeCount += wasLiked ? -1 : 1;

            try {
                await toggleLikeApi('photo', this.photoId, wasLiked);
            } catch {
                this.isLiked = wasLiked;
                this.likeCount += wasLiked ? 1 : -1;
                window.showError?.(window.t ? window.t('common.like_update_failed') : 'Failed to update like');
            } finally {
                this.isLikeLoading = false;
            }
        },

        // --- Comments ---

        openComments() {
            this.commentsOpen = true;
            if (this._commentsStale) {
                this._fetchComments();
            } else {
                this.$nextTick(() => {
                    const container = this._getCommentsContainer();
                    if (container) {
                        window.LucideIcons?.initialize(container);
                        this._bindCommentListeners();
                    }
                });
            }
        },

        async _fetchComments() {
            const container = this._getCommentsContainer();
            if (!container) return;

            const scrollContainer = container.querySelector('.overflow-y-auto');
            if (scrollContainer) scrollContainer.innerHTML = '';

            try {
                const params = new URLSearchParams({
                    entity_type: 'photo',
                    entity_id: this.photoId,
                    feed_owner_id: this._userId,
                    limit: '20'
                });
                const resp = await fetch(`/app/comment/fetch?${params}`);
                if (!resp.ok) throw new Error('fetch failed');
                const html = await resp.text();

                if (scrollContainer) {
                    scrollContainer.innerHTML = html;
                }

                this._commentsStale = false;

                this.$nextTick(() => {
                    window.LucideIcons?.initialize(container);
                    this._bindCommentListeners();
                });
            } catch {
                // Leave empty on error
            }
        },

        _bindCommentListeners() {
            if (this._commentListeners) return;

            // Comment events carry changes that a list observer cannot count reliably.
            const matchesPhoto = (e) => String(e.detail.entityId) === String(this.photoId);

            this._commentListeners = {
                'comment-added': (e) => {
                    if (!matchesPhoto(e)) return;
                    this.commentCount++;
                    this._commentsStale = true;
                    window.LucideIcons?.initialize(this._getCommentsContainer());
                },
                'comment-deleted': (e) => {
                    if (!matchesPhoto(e)) return;
                    const removed = parseInt(e.detail.count, 10) || 1;
                    this.commentCount = Math.max(0, this.commentCount - removed);
                    this._commentsStale = true;
                },
            };

            for (const [name, handler] of Object.entries(this._commentListeners)) {
                window.addEventListener(name, handler);
            }
        },

        /** Platform composers call this from destroy(). */
        _releaseCommentListeners() {
            if (!this._commentListeners) return;
            for (const [name, handler] of Object.entries(this._commentListeners)) {
                window.removeEventListener(name, handler);
            }
            this._commentListeners = null;
        },

        closeComments() {
            this.commentsOpen = false;
        },

        // --- Menu ---

        openMenu() {
            this.menuOpen = true;
        },

        closeMenu() {
            this.menuOpen = false;
        },

        async confirmDelete() {
            if (!confirm(this.deleteConfirmText)) {
                this.closeMenu();
                return;
            }
            this.closeMenu();

            const deleteUrl = `/app/albums?todo=delete_photo&aid=${this._albumId}&pid=${this.photoId}`;
            try {
                const resp = await fetch(deleteUrl);
                if (!resp.ok) throw new Error('Delete failed');
                window.location.href = this.albumUrl;
            } catch {
                window.location.href = deleteUrl;
            }
        },

        openModModal(launcher = null) {
            this.closeMenu();
            if (window.openPhotoModModal) {
                window.openPhotoModModal(launcher);
            } else {
                window.dispatchEvent(new CustomEvent('open-photo-mod-modal'));
            }
        },

        // --- Owner Actions: Set Cover ---

        async setAsCover() {
            if (this.settingCover || this.isCover) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.settingCover = true;
            this.closeMenu();

            try {
                const formData = new FormData();
                formData.append('album_cover_id', this.photoId);

                const response = await fetch(`/v1/album/${this._albumId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to set cover');

                this.albumCoverId = this.photoId;
            } catch {
                // Silently fail
            } finally {
                this.settingCover = false;
            }
        },

        // --- Owner Actions: Edit Caption ---

        openCaptionEdit() {
            this.closeMenu();
            this.tempCaption = this.description || '';
            this.captionEditOpen = true;
        },

        closeCaptionEdit() {
            this.captionEditOpen = false;
            this.tempCaption = '';
        },

        async saveCaption() {
            if (this.savingCaption) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.savingCaption = true;

            try {
                const formData = new FormData();
                formData.append('description', this.tempCaption);

                const response = await fetch(`/v1/photo/${this.photoId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to save caption');

                this.description = this.tempCaption;
                this.closeCaptionEdit();
            } catch {
                // Silently fail
            } finally {
                this.savingCaption = false;
            }
        },

        // --- Owner Actions: Move to Album ---

        openMoveModal() {
            this.closeMenu();
            this.moveModalOpen = true;
        },

        closeMoveModal() {
            this.moveModalOpen = false;
        },

        async moveToAlbum(targetAlbumId) {
            if (this.movingPhoto || !targetAlbumId) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.movingPhoto = true;

            try {
                const formData = new FormData();
                formData.append('album_id', targetAlbumId);

                const response = await fetch(`/v1/photo/${this.photoId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to move photo');

                this.closeMoveModal();
                this._albumId = String(targetAlbumId);
                this.albumUrl = '/app/album?aid=' + targetAlbumId + '&uid=' + this._userId;

                // Navigate to the new album
                window.location.href = this.albumUrl;
            } catch {
                // Silently fail
            } finally {
                this.movingPhoto = false;
            }
        },

        // --- Owner Actions: Edit Thumbnail Crop ---

        editThumbnail() {
            this.closeMenu();
            window.location.href = `/app/albums/${this._albumId}/edit-contents#crop_${this.photoId}`;
        }
    };
}
